import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import Papa from "papaparse";
import { Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { type Workspace } from "@/store";

interface ImportCSVModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImport: (data: { columns: string[]; rows: Workspace[] }) => void;
}

export function ImportCSVModal({
  open,
  onOpenChange,
  onImport,
}: ImportCSVModalProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [fileName, setFileName] = useState<string>("");

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    setIsProcessing(true);

    try {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          console.log("CSV parse results:", results);

          if (results.errors.length > 0) {
            console.error("CSV parse errors:", results.errors);
            toast.error("CSV parsing errors detected", {
              description: "Some rows may not have been imported correctly.",
            });
          }

          // Extract columns from the first row's keys
          const columns = results.meta.fields || [];
          console.log("Extracted columns:", columns);

          // Convert rows to Workspace objects
          const rows: Workspace[] = results.data.map((row: any, index: number) => {
            const workspace: Workspace = {
              id: (index + 1).toString(),
            };

            // Add all columns from CSV
            columns.forEach((column) => {
              workspace[column] = row[column] || "";
            });

            return workspace;
          });

          console.log(`Imported ${rows.length} workspaces with ${columns.length} columns`);

          // Call the import handler
          onImport({ columns, rows });

          toast.success("CSV imported successfully!", {
            description: `Imported ${rows.length} rows with ${columns.length} columns`,
          });

          setIsProcessing(false);
          setFileName("");
          onOpenChange(false);
        },
        error: (error) => {
          console.error("CSV parse error:", error);
          toast.error("Failed to parse CSV", {
            description: error.message,
          });
          setIsProcessing(false);
          setFileName("");
        },
      });
    } catch (error) {
      console.error("File read error:", error);
      toast.error("Failed to read file", {
        description: error instanceof Error ? error.message : "Unknown error",
      });
      setIsProcessing(false);
      setFileName("");
    }
  };

  const handleCancel = () => {
    setFileName("");
    setIsProcessing(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Import Workspaces from CSV</DialogTitle>
          <DialogDescription>
            Upload a CSV file to import workspaces data. The first row should
            contain column headers.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="csv-file">CSV File</Label>
            <div className="flex items-center gap-2">
              <label
                htmlFor="csv-file"
                className="flex-1 flex items-center justify-center gap-2 px-4 py-8 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 transition-colors"
              >
                <Upload className="h-5 w-5 text-gray-400" />
                <span className="text-sm text-gray-600">
                  {fileName || "Click to select CSV file"}
                </span>
              </label>
              <input
                id="csv-file"
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="hidden"
                disabled={isProcessing}
              />
            </div>
          </div>

          {isProcessing && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Processing CSV file...</span>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={isProcessing}
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
